import { captureException } from '@sentry/react';
import { queryOptions } from '@tanstack/react-query';

import { authorizedAxiosInstance } from '@/clients';

const EXECUTIVE_ROSTER_STALE_TIME = 15 * 60 * 1000;

export interface CompanyExecutiveCompensation {
    acceptedDate?: string;
    allOtherCompensation?: number;
    bonus?: number;
    currency?: string;
    filingDate?: string;
    incentivePlanCompensation?: number;
    optionAward?: number;
    salary?: number;
    stockAward?: number;
    total?: number;
    year?: number;
}

export interface CompanyExecutive {
    active?: boolean;
    avatarUrl?: string;
    compensation?: CompanyExecutiveCompensation;
    currencyPay?: string;
    email?: string;
    gender?: string;
    id: string;
    linkedinUrl?: string;
    location?: string;
    name: string;
    pay?: number;
    phone?: string;
    title?: string;
    titleSince?: number;
    website?: string;
    xUrl?: string;
    yearBorn?: number;
}

const asRecord = (value: unknown): Record<string, unknown> | undefined =>
    value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : undefined;

const optionalString = (value: unknown) =>
    typeof value === 'string' && value.trim() ? value.trim() : undefined;

const optionalNumber = (value: unknown) => {
    const numericValue = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : Number.NaN;

    return Number.isFinite(numericValue) ? numericValue : undefined;
};

const optionalBoolean = (value: unknown) => {
    if (typeof value === 'boolean') {
        return value;
    }
    if (value === 'true' || value === 1) {
        return true;
    }
    if (value === 'false' || value === 0) {
        return false;
    }

    return undefined;
};

const normalizeCompensation = (value: unknown): CompanyExecutiveCompensation | undefined => {
    const compensation = asRecord(value);

    if (!compensation) {
        return undefined;
    }

    const normalized = {
        acceptedDate: optionalString(compensation.acceptedDate),
        allOtherCompensation: optionalNumber(
            compensation.allOtherCompensation ?? compensation.otherCompensation,
        ),
        bonus: optionalNumber(compensation.bonus),
        currency: optionalString(compensation.currency ?? compensation.currencyPay),
        filingDate: optionalString(compensation.filingDate),
        incentivePlanCompensation: optionalNumber(
            compensation.incentivePlanCompensation ?? compensation.nonEquityIncentivePlanCompensation,
        ),
        optionAward: optionalNumber(compensation.optionAward ?? compensation.optionAwards),
        salary: optionalNumber(compensation.salary),
        stockAward: optionalNumber(compensation.stockAward ?? compensation.stockAwards),
        total: optionalNumber(compensation.total ?? compensation.totalCompensation),
        year: optionalNumber(compensation.year),
    };

    return Object.values(normalized).some((item) => item !== undefined) ? normalized : undefined;
};

export const normalizeCompanyExecutive = (value: unknown, index = 0): CompanyExecutive | undefined => {
    const executive = asRecord(value);
    const name = optionalString(executive?.name ?? executive?.fullName);

    if (!executive || !name) {
        return undefined;
    }

    const title = optionalString(executive.title ?? executive.jobTitle);
    const titleSince = optionalNumber(executive.titleSince ?? executive.jobStartYear);

    return {
        active: optionalBoolean(executive.active ?? executive.isActive),
        avatarUrl: optionalString(executive.image ?? executive.avatar ?? executive.photoUrl),
        compensation: normalizeCompensation(executive.compensation),
        currencyPay: optionalString(executive.currencyPay ?? executive.currency),
        email: optionalString(executive.email ?? executive.workEmail),
        gender: optionalString(executive.gender ?? executive.sex),
        id: [name, title, titleSince, index].filter((item) => item !== undefined).join('|'),
        linkedinUrl: optionalString(executive.linkedinUrl ?? executive.linkedin_url),
        location: optionalString(executive.location ?? executive.locationName),
        name,
        pay: optionalNumber(executive.pay),
        phone: optionalString(executive.phone ?? executive.mobilePhone),
        title,
        titleSince,
        website: optionalString(executive.website ?? executive.personalWebsite),
        xUrl: optionalString(executive.xUrl ?? executive.twitterUrl ?? executive.twitter_url),
        yearBorn: optionalNumber(executive.yearBorn ?? executive.birthYear),
    };
};

const fetchCompanyExecutives = async (symbol: string, signal: AbortSignal) => {
    const response = await authorizedAxiosInstance.get<unknown>('/api/vendors/fmp/insiders/roster', {
        params: { symbol: symbol.trim().toLocaleUpperCase() },
        signal,
    });
    const roster = Array.isArray(response.data) ? response.data : [];

    return roster.flatMap((executive, index) => {
        const normalized = normalizeCompanyExecutive(executive, index);

        return normalized ? [normalized] : [];
    });
};

export const companyExecutiveQueryKeys = {
    roster: (symbol: string) => ['vendors', 'fmp', 'company-executives', symbol.trim().toLocaleUpperCase()] as const,
};

export const companyExecutiveQueryOptions = {
    roster: (symbol: string) =>
        queryOptions({
            queryFn: async ({ signal }) => {
                try {
                    return await fetchCompanyExecutives(symbol, signal);
                } catch (error) {
                    if (!signal.aborted) {
                        captureException(error);
                    }

                    throw error;
                }
            },
            queryKey: companyExecutiveQueryKeys.roster(symbol),
            staleTime: EXECUTIVE_ROSTER_STALE_TIME,
        }),
};
